/**
 * Shot timing for `clip-reel`, kept clear of Remotion so it can be unit tested and so the route
 * layer could reuse it without pulling a renderer into the backend.
 *
 * The three second cap is the whole reason the composition exists, so it lives in one function
 * rather than being applied twice — the component and `calculateMetadata` must not be able to
 * disagree about how long a shot is, or the reel ends on a frame that was never rendered.
 */

export const CLIP_REEL_FPS = 30;
/** 3s. The cap, and the point. */
export const CLIP_REEL_MAX_SHOT = 90;
/** 1.2s. Below this a viewer cannot read a place name, so a caller asking for less gets this. */
export const CLIP_REEL_MIN_SHOT = 36;
export const CLIP_REEL_DEFAULT_SECONDS = 2.5;

export type ClipReelShot = {
	/** https URL of an mp4 (Pexels) or a photo (Unsplash/Pexels). */
	src: string;
	/** Defaults to "video" when `src` looks like a video file, so a caller can usually omit it. */
	kind?: "video" | "photo";
	/** Shot length in seconds. Clamped to 1.2–3.0 — asking for more does not get more. */
	seconds?: number;
	/**
	 * Seconds into the source file to start. Curated clips run 4–115s and the usable few seconds are
	 * rarely at the head, so this is what turns a 40 second aerial into one three second shot.
	 * The caller is responsible for `startAt + seconds` fitting inside the source: past the end,
	 * FFmpeg returns the last frame and the shot reads as a freeze.
	 */
	startAt?: number;
	/** Place name, bottom left. Omit on shots meant to run as pure footage. */
	caption?: string;
	/** One short factual line under the caption. */
	note?: string;
	/** e.g. "Pexels · Maksim Romashkin". Rendered small at the foot of the safe area. */
	credit?: string;
};

const VIDEO_SRC = /\.(mp4|mov|webm|m4v)(\?|$)/i;

export const isVideoShot = (shot: ClipReelShot) => (shot.kind ? shot.kind === "video" : VIDEO_SRC.test(shot.src));

/** Frames for one shot, clamped. NaN and negatives fall back to the default rather than to zero. */
export const clipShotFrames = (shot: ClipReelShot, fps: number = CLIP_REEL_FPS) => {
	const seconds = Number.isFinite(shot?.seconds as number) && (shot.seconds as number) > 0 ? (shot.seconds as number) : CLIP_REEL_DEFAULT_SECONDS;
	const raw = Math.round(seconds * fps);
	return Math.min(Math.max(raw, CLIP_REEL_MIN_SHOT), CLIP_REEL_MAX_SHOT);
};

/**
 * Total frames. Never returns 0: a composition with `durationInFrames: 0` is a render error rather
 * than an empty video, and an empty shot list should fail on the props, not on the timeline.
 */
export const clipReelDuration = (shots: ClipReelShot[], fps: number = CLIP_REEL_FPS) =>
	Math.max(
		(shots ?? []).reduce((total, shot) => total + clipShotFrames(shot, fps), 0),
		CLIP_REEL_MIN_SHOT
	);
